import type { ReactNode } from "react";

import type { HybridResult, JobRun } from "../api/types";
import { formatDateTime, formatMaybeCurrency } from "../lib/format";

const KNOWN_SIGNALS = new Set(["BUY", "SELL", "HOLD", "ERROR"]);

interface AgentPanelProps {
  agentResult: HybridResult | null;
  lastRun: JobRun | null;
  children?: ReactNode;
}

function signalClass(signal: string): string {
  if (!KNOWN_SIGNALS.has(signal)) {
    return "signal-pill signal-unknown";
  }
  return `signal-pill signal-${signal.toLowerCase()}`;
}

function runMetaText(run: JobRun | null): string {
  if (!run) {
    return "No worker run has been stored yet.";
  }
  const finishedAt = run.finished_at ? formatDateTime(run.finished_at) : "Running";
  return `Run ${run.id} - ${run.status} - finished ${finishedAt}`;
}

export function AgentPanel({ agentResult, lastRun, children }: AgentPanelProps) {
  const signal = String(agentResult?.signal || "UNKNOWN").toUpperCase();

  const metrics: [string, string][] = agentResult
    ? [
        ["Take profit", formatMaybeCurrency(agentResult.take_profit)],
        ["Stop loss", formatMaybeCurrency(agentResult.stop_loss)],
        [
          "Position size",
          agentResult.position_size_multiplier
            ? `${Number(agentResult.position_size_multiplier).toFixed(2)}x`
            : "N/A",
        ],
        ["Volatility", agentResult.volatility_regime || "N/A"],
      ]
    : [];

  const reasonText =
    agentResult?.reason || agentResult?.error || "No reasoning was stored with this result.";

  return (
    <aside className="agent-panel" aria-label="Agent result">
      <div className="agent-heading">
        <div>
          <p className="panel-eyebrow">Hybrid Agent</p>
          <h2>Latest Signal</h2>
        </div>
        <span className={signalClass(signal)}>{signal}</span>
      </div>
      <div className="agent-content">
        {!agentResult ? (
          <p className="empty-state">No stored agent result for this symbol yet.</p>
        ) : (
          <>
            <div className="metric-grid">
              {metrics.map(([label, value]) => (
                <div className="metric" key={label}>
                  <span className="metric-label">{label}</span>
                  <span className="metric-value">{value}</span>
                </div>
              ))}
            </div>
            <div className="reason-block">{reasonText}</div>
          </>
        )}
      </div>
      <div className="run-meta">{runMetaText(lastRun)}</div>
      {children}
    </aside>
  );
}
